import { type FC, useCallback, useState } from "react";

import { Button, Control } from "@snapchat/snap-design-system";
import { DownloadIcon } from "@snapchat/snap-design-system-icons";

import { Flexbox } from "../../../../shared/components";
import { useToggleState } from "../../../../shared/hooks";
import { errorTextContainer } from "../../../../shared/styles";
import { downloadLensScanCode } from "../../../effects";
import { type ScanCode } from "../../../utils/getLensScanCode";

import {
    AddButtonContainer,
    QrCodeImage,
    ScanCodeModal,
    ScanCodeTrigger,
    ScanCodeTriggerImage,
    scanCodeControl,
} from "./LensScanCode.styles";
import { useScanCodeMessages } from "./useScanCodeMessages";

export interface LensScanCodeProps {
    scanCode: ScanCode;
    isVisibleDownload: boolean;
}

export const LensScanCode: FC<LensScanCodeProps> = ({ scanCode, isVisibleDownload }) => {
    const [modalProps, showModal] = useToggleState();
    const [downloadFailed, setDownloadFailed] = useState(false);
    const text = useScanCodeMessages();

    const onClickDownload = useCallback(() => {
        setDownloadFailed(false);
        downloadLensScanCode(scanCode).catch(() => {
            setDownloadFailed(true);
        });
    }, [scanCode]);

    return (
        <>
            <Control label={text.label} className={scanCodeControl}>
                <Flexbox alignItems="center">
                    <ScanCodeTrigger aria-controls="ScanCodeModal" aria-label={text.open} onClick={showModal}>
                        <ScanCodeTriggerImage src={scanCode.previewUrl} alt={text.previewAlt} />
                    </ScanCodeTrigger>
                    {isVisibleDownload && (
                        <AddButtonContainer>
                            <Button shape="circle" onClick={onClickDownload} aria-label={text.download}>
                                <DownloadIcon />
                            </Button>
                        </AddButtonContainer>
                    )}
                </Flexbox>
                {downloadFailed && (
                    <span role="alert" className={errorTextContainer}>
                        {text.downloadError}
                    </span>
                )}
            </Control>
            <ScanCodeModal {...modalProps} padBody={false} footer={false} width={"auto"} id="ScanCodeModal">
                <QrCodeImage src={scanCode.previewUrl} alt={text.imageAlt} />
            </ScanCodeModal>
        </>
    );
};
